import { tryCatch } from "@/utils/try-catch";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useState, ChangeEvent, useContext, useEffect, useCallback } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig, FlowType } from "@/config/ValidatePasswordFlowConfig";

import { genericService } from "@/services/GenericService";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { validatePasswordService } from "@/services/shared/ValidatePasswordService";

import { FreshStartRequest, FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";
import { FormError } from "@/models/continente-credentials/common/FormError"
import { ValidatePasswordRequest } from "@/models/shared/ValidatePasswordRequest";
import { SkipAssociationResponse } from "@/models/shared/SkipAssociationResponse";
import { ValidatePasswordResponse } from "@/models/shared/ValidatePasswordResponse";
import { ValidatePassword } from "@/models/continente-credentials/mobile/ValidatePassword";

import { divideEmail } from "@/utils/EmailDivider";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import CredentialsInput from "@/components/ui/Input";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import TermsAndConditionsCheckbox from "@/components/terms-and-conditions-checkbox/TermsAndConditionsCheckbox";
import ForgotPasswordButton from "@/components/forgot-password-button/forgot-password-button";
import FreshStartButton from "@/components/fresh-start-button/fresh-start-button";
import Divider from "@/components/divider";

import { UserContext } from "@/contexts/continente-credentials/UserContext";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { cn } from "@/utils/utils";
import { siteKey } from "@/hooks/continente-credentials/useGoogleRecaptcha";
import { recoverPasswordService } from "@/services/shared/RecoverPasswordService";
import { CaptchaHookReturn, useCaptcha } from "@/hooks/continente-credentials/useCaptcha";
import AltchaCaptcha from "@/components/altcha-captcha/AltchaCaptcha";
import { useGoToFaq } from "@/hooks/useGoToFaq";
import { analyticsService } from "@/services/AnalyticsService";

const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "";

interface ValidatePasswordPageProps {
  flowConfig: FlowConfig;
}

type Form = ValidatePassword & FormError;

export default function ValidatePasswordPage({ flowConfig }: ValidatePasswordPageProps) {
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();
  let captcha: CaptchaHookReturn | null = null;

  const userContext = useContext(UserContext);
  const digitalAssetContext = useContext(DigitalAssetContext);
  const { handleResponse } = useApiResponseHandler();
  const goToFaq = useGoToFaq();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [labelPasswordVisible, setLabelPasswordVisible] = useState(false);
  const [checkAcceptedTermsConditions, setAcceptedTermsConditions] = useState(true);
  const [isAdminBlocked, setAdminBlocked] = useState(false);

  const userData = location.state?.properties || {};
  const mustAcceptTermsConditions = userData.mustAcceptTermsConditions || false;
  const phoneFreshStartEligible = userData.phoneFreshStartEligible === true;
  const userIdentifier = flowConfig.identifierField ? userData[flowConfig.identifierField] || "" : "";

  const nextStep = location.state?.nextStep ?? "api" + location.pathname;
  const isDeepLink = location.state?.from == "dl";

  const requiredAssociation =
    (flowConfig.flow === AuthFlowType.PHONE_LOGIN_EMAIL_ASSOCIATION &&
      digitalAssetContext.digitalAsset.requiredEmailAssociation) ||
    (flowConfig.flow === AuthFlowType.EMAIL_LOGIN_PHONE_NUMBER_ASSOCIATION &&
      digitalAssetContext.digitalAsset.requiredPhoneNumberAssociation) ||
    (flowConfig.flow === AuthFlowType.EMAIL_REGISTRATION_PHONE_ASSOCIATION &&
      digitalAssetContext.digitalAsset.requiredPhoneNumberAssociation);

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleGoogleChallenge = useCallback(async () => {
    setLoading(true)
    const { data: response, error: someError } = await tryCatch(validatePasswordService.validatePassword(
      {
        passwordRecover: true,
        password: "",
        acceptedTermsConditions: false,
        recaptchaToken: captcha?.getCaptchaToken(),
      },
      nextStep
    ));

    if (someError) {
      setError("submitError", { message: t("common.unexpected_error") });
      setLoading(false);
      return;
    }

    switch (response?.kind) {
      case "success":
        history.push(response.value.nextStep.substring(4), response.value);
        break;

      case "error":
        setError("submitError", { message: t("common.unexpected_error") });
        break;

      default:
        setError("submitError", { message: t("common.unexpected_error") });
        break;
    }
  }, [history, location.state, setError, t]);

  if (flowConfig.useCaptcha) {
    captcha = useCaptcha(location.state);
    captcha.useGoogleRecaptcha(handleGoogleChallenge);
  }

  const handlePasswordChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    setPassword(value);
    setValue("password", value);
    setLabelPasswordVisible(!!value);
  };

  const handleAcceptedTermsConditionsChange = () => {
    const newValue = checkAcceptedTermsConditions;
    setAcceptedTermsConditions(newValue);
    setValue("acceptedTermsConditions", newValue);
  };

  useEffect(() => {
    register("password", {
      required: { value: true, message: t("common.required_field") },
    });

    if (mustAcceptTermsConditions) {
      register("acceptedTermsConditions", {
        required: { value: true, message: "" },
      });
    }
  }, [mustAcceptTermsConditions, register, t]);

  const passwordRecover = async () => {
    setLoading(true);
    analyticsService.trackPasswordRecoverInitiated(flowConfig.flow);

    if (flowConfig.type === FlowType.SITE_CREDENTIALS) {
      const { data: response, error } = await tryCatch(
        recoverPasswordService.recoverPassword(
          "api/site-credentials/password/recover/validate-identifier"
        )
      );
      handleResponse(response, { setError }, error);
      setLoading(false);
      return;
    }

    const { data: response, error } = await tryCatch(
      validatePasswordService.validatePassword(
        {
          passwordRecover: true,
          password: "",
          acceptedTermsConditions: false,
          identifier: userIdentifier,
        },
        nextStep
      )
    );

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  const freshStart = async () => {
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse, FreshStartRequest>(
      "api/phonenumber/login/fresh-start",
      {
        origin: flowConfig.freshStartOrigin
      }
    ));

    handleResponse(response, { setError }, error);
    setLoading(false);
  };

  const skipAssociation = async () => {
    if (flowConfig.type !== FlowType.ASSOCIATION || requiredAssociation) return;

    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<SkipAssociationResponse, ValidatePasswordRequest>(
      nextStep,
      {
        skipAssociation: true,
        password: "",
        acceptedTermsConditions: false
      }
    ));

    handleResponse(
      response,
      {
        setError,
        onSuccess: (response) => {
          userContext.updateUser({
            ...userContext.user,
            email: "",
          });
          history.push(response.value.nextStep.substring(4), response.value);
        },
      },
      error
    );

    setLoading(false);
  };

  const onSubmit: SubmitHandler<ValidatePassword> = async (data) => {
    setLoading(true);
    analyticsService.trackPasswordSubmitted(flowConfig.flow);

    const { data: response, error } = await tryCatch(genericService.post<ValidatePasswordResponse, ValidatePasswordRequest>(
      nextStep,
      {
        password: data.password,
        acceptedTermsConditions: data.acceptedTermsConditions,
      }
    ));

    if (response?.kind === 'error') {
      const errorCode = response.code || 'invalid_password';
      analyticsService.trackLoginFailure(flowConfig.flow, errorCode);
    }

    handleResponse(
      response,
      {
        setError,
        setAdminBlocked,
        onSuccess: (response) => {
          analyticsService.trackLoginSuccess(flowConfig.flow);

          userContext.updateUser({
            ...userContext.user,
            ...(flowConfig.identifierField
              ? { [flowConfig.identifierField as string]: data.identifier?.trim() ?? "" }
              : {}),
          });
          history.push(response.value.nextStep.substring(4), response.value);
        },
      },
      error
    );

    setLoading(false);
  };

  return (
    <CredentialsForm
      id={`validate_password_form_${flowConfig.flow}`}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(
          onSubmit,
          // (errors) => {
          //   console.log("Submit errors:", errors);
          // }
        )(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={cn(
          t(flowConfig.labels.headerSubtitle ?? ""),
          (flowConfig.identifierField == "email" ? divideEmail(userIdentifier) : userIdentifier))
        }
        showLoading={loading}
        previousStepBackXTimes={1}
        showBackButton={!isDeepLink && flowConfig.showBackButton}
      />
      <CredentialsInput
        id="password_input"
        name="password_input"
        type={"password"}
        value={password}
        disabled={loading}
        onChange={handlePasswordChange}
        required={true}
        labelText={t(flowConfig.labels.input)}
        isFilled={labelPasswordVisible}
        isError={!!(errors.password || errors.blocked || errors.submitError)}
        errorMsg={
          errors.password?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        errorMsgBlocked={errors.blocked ? errors.blocked.message : ""}
        autoComplete="current-password"
        onInvalid={(e) => {
          e.preventDefault()
          if (e.target.validity.valueMissing) {
            setError("password", { message: t("common.required_field") });
          }
        }}
        ariaLabel={requiredAssociation ?
          t(flowConfig.labels.inputAria.required) :
          t(flowConfig.labels.inputAria.notRequired ?? "")
        }
      />
      {errors.blocked && isAdminBlocked && (
        <div className="h-10 text-7xl font-bold text-center mb-30">
          <span
            onClick={() => goToFaq(ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION)}
            className="text-black hover:underline cursor-pointer font-bold"
          >
            {" "}
            {t("common.contact_form")}{" "}
          </span>
        </div>
      )}
      {flowConfig.showForgotPassword && (
        <ForgotPasswordButton onClick={passwordRecover} />
      )}
      {mustAcceptTermsConditions && (
        <TermsAndConditionsCheckbox
          onClick={handleAcceptedTermsConditionsChange}
          isError={errors.acceptedTermsConditions ? true : false}
        />
      )}
      <div className="h-full w-full flex flex-col gap-4 justify-center items-center">
        <Button
          disabled={loading}>
          {t(flowConfig.labels.nextButton)}
        </Button>
        {flowConfig.useCaptcha && captcha?.shouldShowRecaptcha && (
          <div
            id="g-recaptcha"
            className="flex justify-center"
            data-sitekey={siteKey}
          />
        )}
        {flowConfig.useCaptcha && captcha?.shouldShowAltcha && (
          <AltchaCaptcha
            onVerified={captcha.handleAltchaCaptchaVerified}
            onError={captcha.handleAltchaCaptchaError}
          />
        )}
        {flowConfig.type === FlowType.ASSOCIATION && !requiredAssociation && (
          <Button
            type="button"
            variant={"secondary"}
            onClick={skipAssociation}
            disabled={loading}>
            {t(flowConfig.labels.skipButton ?? "")}
          </Button>
        )}
        {flowConfig.showFreshStart && phoneFreshStartEligible && (
          <>
            <Divider title={t("common.or")} />
            <FreshStartButton onClick={freshStart} />
          </>
        )}
        <NeedHelpButton />
      </div>
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <CredentialsFooter />
      </div>
    </CredentialsForm>
  );
}

export const createValidatePasswordPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <ValidatePasswordPage flowConfig={config} />;
};

export const EmailLoginValidatePassword = createValidatePasswordPage(AuthFlowType.EMAIL_LOGIN);
export const EmailLoginValidatePasswordPhoneNumberAssociation = createValidatePasswordPage(AuthFlowType.EMAIL_LOGIN_PHONE_NUMBER_ASSOCIATION);
export const PhonenumberLoginValidatePassword = createValidatePasswordPage(AuthFlowType.PHONE_LOGIN);
export const PhonenumberLoginValidatePasswordEmailAssociation = createValidatePasswordPage(AuthFlowType.PHONE_LOGIN_EMAIL_ASSOCIATION);
export const PhonenumberRegistrationValidatePasswordEmailAssociation = createValidatePasswordPage(AuthFlowType.PHONE_REGISTRATION_EMAIL_ASSOCIATION);
export const EmailRegistrationValidatePasswordPhoneAssociation = createValidatePasswordPage(AuthFlowType.EMAIL_REGISTRATION_PHONE_ASSOCIATION);
export const SRTValidatePassword = createValidatePasswordPage(AuthFlowType.SRT);
export const SCUpdatePWValidatePassword = createValidatePasswordPage(AuthFlowType.SITE_CREDENTIALS_PW);
export const SCUpdatePhoneValidatePassword = createValidatePasswordPage(AuthFlowType.SITE_CREDENTIALS_PHONE);
export const SCUpdateEmailValidatePassword = createValidatePasswordPage(AuthFlowType.SITE_CREDENTIALS_EMAIL);
export const SCConsentsValidatePassword = createValidatePasswordPage(AuthFlowType.SITE_CREDENTIALS_CONSENTS);
